import apiClient from './apiClient';
import i18n from 'i18next';

export interface BalloonCreateRequest {
  factoryNumber: number;
  balloonCode: number;
  emptyWeight: number;
  gasWeight: number;
  material: 'STEEL' | 'COMPOSITE';
  country: 'KAZAKHSTAN' | 'OTHER';
  productionDate: string; // yyyy-MM-dd
  workingPressure: number;
  testPressure: number;
  lastInspectionDate: string; // yyyy-MM-dd
  lastRepairDate: string; // yyyy-MM-dd
  repairResponsible: string;
  nextInspectionDate: string; // yyyy-MM-dd
}

type SortDirection = 'ASC' | 'DESC';

export type BalloonListFilters = {
  factoryNumber?: number | string | null;
  balloonCode?: number | string | null;
  emptyWeightFrom?: number | string | null;
  emptyWeightTo?: number | string | null;
  gasWeightFrom?: number | string | null;
  gasWeightTo?: number | string | null;
  material?: { value: 'STEEL' | 'COMPOSITE' } | 'STEEL' | 'COMPOSITE' | null;
  country?: { value: 'KAZAKHSTAN' | 'OTHER' } | 'KAZAKHSTAN' | 'OTHER' | null;
  productionDateFrom?: string | null; // yyyy-MM or yyyy-MM-dd
  productionDateTo?: string | null;   // yyyy-MM or yyyy-MM-dd
  workingPressureFrom?: number | string | null;
  workingPressureTo?: number | string | null;
  testPressureFrom?: number | string | null;
  testPressureTo?: number | string | null;
  lastInspectionDateFrom?: string | null; // yyyy-MM or yyyy-MM-dd
  lastInspectionDateTo?: string | null;   // yyyy-MM or yyyy-MM-dd
  lastRepairDateFrom?: string | null;     // yyyy-MM or yyyy-MM-dd
  lastRepairDateTo?: string | null;       // yyyy-MM or yyyy-MM-dd
  repairResponsible?: string | null;
  nextInspectionDateFrom?: string | null; // yyyy-MM or yyyy-MM-dd
  nextInspectionDateTo?: string | null;   // yyyy-MM or yyyy-MM-dd
};

interface LooseObject {
  [key: string]: any;
}

export async function createBalloon(payload: BalloonCreateRequest): Promise<any> {
  try {
    const response = await apiClient.post(
      `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/balloons`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language?.includes('ru') ? 'ru-RU' : 'kk-KZ',
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error during balloon creation:', error);
    throw error;
  }
}

export async function getBalloonsList(
  page: number = 1,
  size: number = 10,
  sortDirection: SortDirection = 'DESC',
  filters?: BalloonListFilters,
): Promise<any> {
  try {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sortDirection,
    }).toString();

    const body: LooseObject = {};

    if (filters?.factoryNumber) {
      body.factoryNumber = filters.factoryNumber;
    }
    if (filters?.balloonCode) {
      body.balloonCode = filters.balloonCode;
    }
    if (filters?.emptyWeightFrom) {
      body.emptyWeightFrom = filters.emptyWeightFrom;
    }
    if (filters?.emptyWeightTo) {
      body.emptyWeightTo = filters.emptyWeightTo;
    }
    if (filters?.gasWeightFrom) {
      body.gasWeightFrom = filters.gasWeightFrom;
    }
    if (filters?.gasWeightTo) {
      body.gasWeightTo = filters.gasWeightTo;
    }
    if (filters?.material) {
      body.material = typeof filters.material === 'object' && 'value' in filters.material
        ? (filters.material as any).value
        : filters.material;
    }
    if (filters?.country) {
      body.country = typeof filters.country === 'object' && 'value' in filters.country
        ? (filters.country as any).value
        : filters.country;
    }
    if (filters?.productionDateFrom) {
      body.productionDateFrom = filters.productionDateFrom;
    }
    if (filters?.productionDateTo) {
      body.productionDateTo = filters.productionDateTo;
    }
    if (filters?.workingPressureFrom) {
      body.workingPressureFrom = filters.workingPressureFrom;
    }
    if (filters?.workingPressureTo) {
      body.workingPressureTo = filters.workingPressureTo;
    }
    if (filters?.testPressureFrom) {
      body.testPressureFrom = filters.testPressureFrom;
    }
    if (filters?.testPressureTo) {
      body.testPressureTo = filters.testPressureTo;
    }
    if (filters?.lastInspectionDateFrom) {
      body.lastInspectionDateFrom = filters.lastInspectionDateFrom;
    }
    if (filters?.lastInspectionDateTo) {
      body.lastInspectionDateTo = filters.lastInspectionDateTo;
    }
    if (filters?.lastRepairDateFrom) {
      body.lastRepairDateFrom = filters.lastRepairDateFrom;
    }
    if (filters?.lastRepairDateTo) {
      body.lastRepairDateTo = filters.lastRepairDateTo;
    }
    if (filters?.repairResponsible) {
      body.repairResponsible = filters.repairResponsible;
    }
    if (filters?.nextInspectionDateFrom) {
      body.nextInspectionDateFrom = filters.nextInspectionDateFrom;
    }
    if (filters?.nextInspectionDateTo) {
      body.nextInspectionDateTo = filters.nextInspectionDateTo;
    }

    const response = await apiClient.post(
      `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/balloons/list?${params}`,
      body,
      {
        headers: {
          'Accept-Language': i18n.language?.includes('ru') ? 'ru-RU' : 'kk-KZ',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching balloons list:', error);
    throw error;
  }
}

export async function getBalloonById(id: number | string): Promise<any> {
  try {
    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/balloons/${id}`,
      {
        headers: {
          'Accept-Language': i18n.language?.includes('ru') ? 'ru-RU' : 'kk-KZ',
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching balloon by id ${id}:`, error);
    throw error;
  }
}

export async function updateBalloon(id: number | string, payload: Partial<BalloonCreateRequest> | any): Promise<any> {
  try {
    const response = await apiClient.put(
      `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/balloons/${id}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language?.includes('ru') ? 'ru-RU' : 'kk-KZ',
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating balloon with id ${id}:`, error);
    throw error;
  }
}

export async function deleteBalloon(id: number | string): Promise<any> {
  try {
    const response = await apiClient.delete(
      `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/balloons/${id}`,
      {
        headers: {
          'Accept-Language': i18n.language?.includes('ru') ? 'ru-RU' : 'kk-KZ',
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching balloon by id ${id}:`, error);
    throw error;
  }
}

export async function checkBalloonCode(code: number | string): Promise<any> {
  try {
    const response = await apiClient.get(
      `${process.env.REACT_APP_URL_BALLOONS_SERVICE}/balloons/check-code`,
      {
        params: { balloonCode: code },
        headers: {
          'Accept-Language': i18n.language?.includes('ru') ? 'ru-RU' : 'kk-KZ',
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    // rethrow to be caught by UI for modal display
    throw error;
  }
}

